import { useId, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { T, useGT } from 'gt-react';
import { CheckIcon, PlusIcon, XIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  UPLOAD_COLUMNS,
  type UploadLayout,
  type UploadLayoutDefinition,
} from '@erp/rpc';

import { orpc } from '@/api/rpc/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LayoutFileSettings } from './LayoutFileSettings';
import { LayoutPreview } from './LayoutPreview';
import {
  LOOKUP_ERROR_KEY,
  definitionFromDraft,
  emptyMappingDraft,
  type LayoutDraft,
  type MappingDraft,
} from './layoutForm';
import { MappingRow } from './MappingRow';
import { columnIndex, type ResultsSheet } from './sheetCells';

interface Props {
  /** The layout being edited, or null for a new one. */
  layoutId: string | null;
  initial: LayoutDraft;
  sheet: ResultsSheet | null;
  /** The dropped second file, for previewing a layout that reads one. */
  lookupSheet: ResultsSheet | null;
  onSaved: (layout: UploadLayout) => void;
  onCancel: () => void;
}

function nextFreeColumn(draft: LayoutDraft) {
  const used = new Set(draft.columns.map(({ column }) => column));
  return UPLOAD_COLUMNS.find((column) => !used.has(column)) ?? 'A';
}

/** Mutation errors go to a toast; nothing here depends on the component. */
function showError(error: Error): void {
  toast.error(error.message);
}

/** Creates or updates the layout, refreshing the list before `onSaved`. */
function useSaveLayout(
  layoutId: string | null,
  onSaved: (layout: UploadLayout) => void,
) {
  const queryClient = useQueryClient();
  async function saved({ layout }: { layout: UploadLayout }): Promise<void> {
    await queryClient.invalidateQueries({
      queryKey: orpc.pricing.uploadLayouts.list.key(),
    });
    onSaved(layout);
  }
  const create = useMutation({
    ...orpc.pricing.uploadLayouts.create.mutationOptions(),
    onSuccess: saved,
    onError: showError,
  });
  const update = useMutation({
    ...orpc.pricing.uploadLayouts.update.mutationOptions(),
    onSuccess: saved,
    onError: showError,
  });
  function save(name: string, definition: UploadLayoutDefinition): void {
    if (layoutId) update.mutate({ id: layoutId, name, definition });
    else create.mutate({ name, definition });
  }
  return { save, isSaving: create.isPending || update.isPending };
}

/**
 * Edits one supplier layout: which input column (or fixed value, row number,
 * flag or formula) fills each upload column, with a live preview on the
 * dropped file.
 */
export function LayoutEditor({
  layoutId,
  initial,
  sheet,
  lookupSheet,
  onSaved,
  onCancel,
}: Props) {
  const gt = useGT();
  const headerListId = useId();
  const [draft, setDraft] = useState(initial);
  const [showErrors, setShowErrors] = useState(false);

  const built = definitionFromDraft(draft);
  const definition = 'definition' in built ? built.definition : null;
  const errors = 'errors' in built ? built.errors : new Map();

  const { save, isSaving } = useSaveLayout(layoutId, onSaved);

  function changeRow(key: string, changes: Partial<MappingDraft>): void {
    setDraft((current) => ({
      ...current,
      columns: current.columns.map((row) =>
        row.key === key ? { ...row, ...changes } : row,
      ),
    }));
  }

  function handleSave(): void {
    setShowErrors(true);
    if (!definition || draft.name.trim() === '') return;
    save(draft.name, definition);
  }

  const rows = [...draft.columns].sort(
    (a, b) => columnIndex(a.column) - columnIndex(b.column),
  );

  return (
    <div className="flex flex-col gap-3">
      <datalist id={headerListId}>
        {(sheet?.headers ?? []).map((header) => (
          <option key={header} value={header}>
            {header}
          </option>
        ))}
      </datalist>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="h-8 w-56"
          placeholder={gt('Supplier name', {
            context: 'placeholder for an upload layout name',
          })}
          value={draft.name}
          onChange={(event) => setDraft({ ...draft, name: event.target.value })}
        />
        <span className="text-muted-foreground text-xs">
          <T context="label before the upload layout's row filter headers">
            Write a row only when one of these input columns is filled:
          </T>
        </span>
        <Input
          className="h-8 min-w-64 flex-1"
          list={headerListId}
          placeholder={gt('e.g. LOAD UNDER AS400 ITEM, LOAD UNDER AS400 PCSC', {
            context: 'placeholder for the upload layout row filter headers',
          })}
          value={draft.includeWhenAnyFilled}
          onChange={(event) =>
            setDraft({ ...draft, includeWhenAnyFilled: event.target.value })
          }
        />
      </div>

      <LayoutFileSettings
        draft={draft}
        headerListId={headerListId}
        lookupError={showErrors ? errors.get(LOOKUP_ERROR_KEY) : undefined}
        onChange={(changes) =>
          setDraft((current) => ({ ...current, ...changes }))
        }
      />

      <div className="rounded-md border px-3">
        {rows.map((row) => (
          <MappingRow
            key={row.key}
            draft={row}
            error={showErrors ? errors.get(row.key) : undefined}
            headerListId={headerListId}
            onChange={(changes) => changeRow(row.key, changes)}
            onRemove={() =>
              setDraft((current) => ({
                ...current,
                columns: current.columns.filter(({ key }) => key !== row.key),
              }))
            }
          />
        ))}
        {rows.length === 0 ? (
          <p className="text-muted-foreground py-2 text-sm">
            <T context="empty upload layout">
              No columns mapped yet; unmapped upload columns are written blank.
            </T>
          </p>
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() =>
            setDraft((current) => ({
              ...current,
              columns: [
                ...current.columns,
                emptyMappingDraft(nextFreeColumn(current)),
              ],
            }))
          }
        >
          <PlusIcon className="h-4 w-4" />
          <T context="button that adds a column mapping to an upload layout">
            Add Column
          </T>
        </Button>
        <div className="flex-1" />
        <Button
          size="sm"
          variant="ghost"
          onClick={onCancel}
          disabled={isSaving}
        >
          <XIcon className="h-4 w-4" />
          <T context="button that closes the upload layout editor">Cancel</T>
        </Button>
        <Button size="sm" onClick={handleSave} disabled={isSaving}>
          <CheckIcon className="h-4 w-4" />
          <T context="button that saves an upload layout">Save Layout</T>
        </Button>
      </div>
      {showErrors && draft.name.trim() === '' ? (
        <p className="text-destructive text-xs">
          <T context="upload layout editor validation error">
            Enter the supplier name.
          </T>
        </p>
      ) : null}

      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-medium">
          <T context="heading of the upload layout preview">Preview</T>
        </h3>
        <LayoutPreview
          sheet={sheet}
          lookupSheet={lookupSheet}
          definition={definition}
        />
      </div>
    </div>
  );
}
